import { View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Trophy, PlusCircle, UserPlus, LogOut } from 'lucide-react-native';

import { useTheme } from '@/lib/styling';
import { Theme } from '@/theme';
import { colors } from '@/lib/theme';
import { Box, Text } from '@/components/ui/primitives';
import { Button } from '@/components/ui/Button';
import { getCreateLeagueHref, getJoinLeagueHref, getLandingHref } from '@/lib/routes';
import { betterAuthClient } from '@/lib/betterAuthClient';
import { clientLogger } from '@/lib/datadog';
import { useQueryClient } from '@tanstack/react-query';

export default function IntroScreen() {
  const router = useRouter();
  const theme = useTheme<Theme>();
  const insets = useSafeAreaInsets();
  const queryClient = useQueryClient();

  const handleLogout = async () => {
    try {
      const result = await betterAuthClient.signOut();
      if (result.error) {
        throw new Error(result.error.message || 'Logout failed');
      }
    } catch (error) {
      clientLogger.error('Logout failed', { error });
    } finally {
      queryClient.clear();
      router.replace(getLandingHref());
    }
  };

  return (
    <View style={[styles.container, { paddingTop: insets.top, paddingBottom: insets.bottom }]}>
      <View style={styles.header}>
        <Button variant="ghost" size="sm" onPress={handleLogout}>
          <LogOut size={18} color={theme.colors.mutedForeground} />
        </Button>
      </View>

      <Box flex={1} alignItems="center" justifyContent="center" paddingHorizontal="xl">
        <Box
          alignItems="center"
          justifyContent="center"
          backgroundColor="primary"
          style={styles.iconContainer}
        >
          <Trophy size={48} color={theme.colors.press} />
        </Box>

        <Text variant="h1" textAlign="center" marginBottom="s">
          Welcome to PCKM
        </Text>
        <Text variant="body" color="mutedForeground" textAlign="center" marginBottom="xl">
          You aren&apos;t in any leagues yet. Join an existing league with an invite code or create a new one to get started.
        </Text>

        <Box width="100%" gap="m">
          <Button
            size="lg"
            onPress={() => router.push(getJoinLeagueHref())}
          >
            <Box flexDirection="row" alignItems="center" justifyContent="center" gap="s">
              <UserPlus size={20} color={theme.colors.press} />
              <Text variant="button">Join a League</Text>
            </Box>
          </Button>

          <Button
            variant="outline"
            size="lg"
            onPress={() => router.push(getCreateLeagueHref())}
          >
            <Box flexDirection="row" alignItems="center" justifyContent="center" gap="s">
              <PlusCircle size={20} color={theme.colors.press} />
              <Text variant="button">Create a League</Text>
            </Box>
          </Button>
        </Box>
      </Box>
    </View>
  );
}

const styles = StyleSheet.create(theme => ({
  container: {
    backgroundColor: theme.colors.background,
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  iconContainer: {
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 3,
    elevation: 4,
    height: 96,
    marginBottom: 24,
    shadowColor: colors.ink.press,
    shadowOffset: { width: 8, height: 8 },
    shadowOpacity: 1,
    shadowRadius: 0,
    width: 96,
  },
}));
